import { useEffect, useState, useRef, useCallback } from "react";
import { applyFiltersToURL } from "../pages/public";
import axios, { AxiosError, CancelTokenSource } from "axios";
import { convertToWildcardFormat } from "../util/filterStateManager";

export const can_use_alternate_request = (filters: {
  [key: string]: string[];
}) => {
  const filters_str = applyFiltersToURL({
    params: new URLSearchParams(),
    filtersFromUrl: filters,
  }).toString();
  const filters_size = new TextEncoder().encode(filters_str).length;
  return filters_size > 2000;
};

export function useAlternateRequest<TData>({
  filters,
  filterLabels,
  url,
  canFetch = true,
  availableFilters = {},
}: {
  filters: { [key: string]: string[] };
  filterLabels: string[];
  url: string;
  canFetch?: boolean;
  availableFilters?: {
    [filterType: string]: { name: string; id?: string | null }[];
  };
}) {
  const [data, setData] = useState<TData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AxiosError>();
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const lastRequestRef =
    useRef<{ filters: { [key: string]: string[] }; url: string } | null>(null);

  const fetchData = useCallback(async () => {
    // Cancel any existing request
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("New request initiated");
    }

    // Create new cancel token
    const cancelToken = axios.CancelToken.source();
    cancelTokenRef.current = cancelToken;

    setIsLoading(true);
    setError(undefined);

    try {
      const filtersToSend: { [key: string]: string[] } = {};
      const searchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (filterLabels.includes(key)) {
          filtersToSend[key] = value;
        } else {
          value.forEach((item) => {
            searchParams.append(key, item);
          });
        }
      });

      // Convert to new API format with wildcard and exclude support
      const { filters: newFormatFilters, exclude_filters } =
        convertToWildcardFormat(filtersToSend, availableFilters);

      // Build request body with new format
      const requestBody: {
        filters: Record<string, string[] | "*">;
        exclude_filters?: Record<string, string[]>;
      } = {
        filters: newFormatFilters,
      };
      if (exclude_filters) {
        requestBody.exclude_filters = exclude_filters;
      }

      const { data: responseData } = await axios.post<TData>(
        url + "?" + searchParams.toString(),
        requestBody,
        {
          cancelToken: cancelToken.token,
        }
      );
      setData(responseData);
    } catch (error) {
      if (!axios.isCancel(error)) {
        setError(error as AxiosError);
      }
    } finally {
      setIsLoading(false);
      cancelTokenRef.current = null;
    }
  }, [filterLabels, filters, url, availableFilters]);

  const mutate = useCallback(
    async (data?: TData) => {
      if (data !== undefined) {
        setData(data);
      }
      await fetchData();
    },
    [fetchData]
  );

  useEffect(() => {
    // Check if filters or URL have actually changed
    const hasChanged =
      !lastRequestRef.current ||
      JSON.stringify(lastRequestRef.current.filters) !==
        JSON.stringify(filters) ||
      lastRequestRef.current.url !== url;

    if (hasChanged && canFetch && filterLabels.length > 0) {
      lastRequestRef.current = { filters, url };
      fetchData();
    }
  }, [filters, url, fetchData, canFetch, filterLabels]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  return { data, isLoading, error, mutate };
}
